<!-- Results. What lit up — never what was missed — and Play again as the
     largest thing on the screen. -->
<template>
  <IonPage>
    <AppHeader title="Round over" back="/" />

    <IonContent :fullscreen="true">
      <div v-if="result" class="soc-stack results-stack">
        <div class="result-headline">
          <p class="big tnum">{{ result.homesLit }} of {{ result.homesTotal }}</p>
          <p class="sub">{{ result.homesLit === 1 ? 'home' : 'homes' }} lit on {{ run?.level?.name ?? 'the street' }}</p>
          <span v-if="result.newBest" class="new-best">New best!</span>
        </div>

        <div class="result-stats" role="list">
          <div class="result-stat" role="listitem"><strong class="tnum">{{ shownScore }}</strong><span>Score</span></div>
          <div class="result-stat" role="listitem"><strong class="tnum">{{ result.bulbsLit }}</strong><span>Bulbs lit</span></div>
          <div class="result-stat" role="listitem"><strong class="tnum">×{{ result.bestMultiplier }}</strong><span>Best multiplier</span></div>
        </div>

        <p v-if="verdict" class="soc-card verdict">{{ verdict }}</p>

        <p class="soc-muted ion-text-center tnum">
          Personal best {{ formatTotal(personalBest) }}<template v-if="result.practice"> · practice speed rounds don't set bests</template>
        </p>

        <section v-if="moments.length" class="soc-card">
          <h2 class="section-title first">Moments switched on</h2>
          <ul class="moments">
            <li v-for="m in moments" :key="m.id">
              <MomentIcon :id="m.id" />
              <span>{{ m.caption }}<span v-if="m.count > 1" class="moments__count tnum">×{{ m.count }}</span></span>
            </li>
          </ul>
        </section>

        <section v-if="unlocked" class="soc-card unlocked">
          <h2 class="section-title first">Decoration unlocked</h2>
          <div class="deco deco--placed">
            <canvas ref="decoEl" width="96" height="96" aria-hidden="true"></canvas>
            <div>
              <strong>{{ unlocked.name }}</strong>
              <span>{{ unlocked.blurb }}</span>
            </div>
          </div>
          <IonButton class="soc-ghost" @click="router.push({ name: 'my-street' })">Place it on My Street</IonButton>
        </section>

        <section class="soc-card national" aria-live="polite">
          <p class="plus">+1 light for Australia</p>
          <p v-if="nationalTotal !== null" class="soc-muted tnum">
            <strong>{{ formatTotal(nationalTotal) }}</strong> lights so far<template v-if="queued"> · yours will be added when you're back online</template>
          </p>
          <p v-else class="soc-muted">Yours will be added when you're back online.</p>
        </section>

        <div class="actions">
          <IonButton class="soc-play" @click="playAgain">Play again</IonButton>
          <IonButton class="soc-secondary" @click="challengeFriend">Challenge a friend</IonButton>
          <IonButton class="soc-ghost" @click="backToStreet">Back to street</IonButton>
        </div>

        <p class="soc-foot">
          <a class="soc-link" href="https://www.originenergy.com.au" target="_blank" rel="noopener noreferrer">
            Discover the useful things Origin can help with this summer
          </a>
        </p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
/* global performance */
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { IonPage, IonContent, IonButton, useIonRouter } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { AppHeader } from '../core/ui';
import { shareLink } from '../core/export.js';
import { shareOrigin } from '../core/native.js';
import { lastResult, lastRun, progress, national, setNational, formatTotal, reducedMotion } from '../store.js';
import { recordRound } from '../lib/national.js';
import { challengePath } from '../lib/challenge.js';
import { MOMENTS } from '../lib/level-gen.js';
import { DECORATIONS } from '../lib/daily.js';
import { palette } from '../lib/palette.js';
import { drawDecoration } from '../lib/sprites.js';
import MomentIcon from '../components/MomentIcon.vue';

const router = useRouter();
const ionRouter = useIonRouter();
const result = computed(() => lastResult.value);
const run = computed(() => lastRun.value);
const decoEl = ref(null);

// One line per KIND of moment, with a count. Eight homes on a ten-home street
// draw from eight moments, so the raw list repeated "The family call is
// connected." three times — which reads as a bug, not as a celebration.
const moments = computed(() => {
  const counts = new Map();
  for (const id of result.value?.moments ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
  return [...counts].map(([id, count]) => {
    const m = MOMENTS.find((x) => x.id === id);
    return m ? { ...m, count } : null;
  }).filter(Boolean);
});
const unlocked = computed(() => (result.value?.unlocked ? DECORATIONS.find((d) => d.id === result.value.unlocked) : null));
const personalBest = computed(() => {
  const r = result.value;
  if (!r) return 0;
  if (r.mode === 'day' && r.day) return Number(progress.bestByDay[String(r.day)]) || 0;
  return Number(progress.bestFreePlay) || 0;
});
const verdict = computed(() => {
  const r = result.value;
  if (!r || r.mode !== 'challenge' || r.target === null) return '';
  const won = r.homesLit > r.target || (r.homesLit === r.target && r.score > (r.targetScore ?? 0));
  if (won) return `You beat ${r.target} ${r.target === 1 ? 'home' : 'homes'}. Pass it on.`;
  if (r.homesLit === r.target) return `Level on ${r.target} homes — they still have the score. One more go?`;
  return `So close — ${r.homesLit} of ${r.target}. One more go?`;
});

// Score counts up unless reduced motion.
const shownScore = ref('0');
function countUp() {
  const target = result.value?.score ?? 0;
  if (reducedMotion()) { shownScore.value = formatTotal(target); return; }
  const start = performance.now();
  const dur = 1100;
  const tick = (now) => {
    const k = Math.min(1, (now - start) / dur);
    const eased = 1 - (1 - k) ** 3;
    shownScore.value = formatTotal(Math.round(target * eased));
    if (k < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

const nationalTotal = ref(national.value?.total ?? null);
const queued = ref(false);

async function addLight() {
  const r = result.value;
  if (!r || r.nationalRecorded) { nationalTotal.value = national.value?.total ?? null; return; }
  r.nationalRecorded = true;
  const out = await recordRound();
  if (Number.isFinite(out.total)) { nationalTotal.value = out.total; setNational({ total: out.total, fetchedAt: Date.now() }); }
  queued.value = out.queued > 0;
}

function drawDeco() {
  if (!decoEl.value || !unlocked.value) return;
  const c = decoEl.value;
  const ctx = c.getContext('2d');
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  c.width = 48 * dpr; c.height = 48 * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, 48, 48);
  drawDecoration(ctx, palette(), unlocked.value.id, 24, 24, 34, 0);
}

function playAgain() {
  const r = result.value;
  if (!r) return;
  if (r.mode === 'day') router.replace({ name: 'play', params: { mode: 'day', code: String(r.day) } });
  else if (r.mode === 'challenge') router.replace({ name: 'play', params: { mode: 'challenge', code: r.code } });
  else router.replace({ name: 'play', params: { mode: 'free' } });
}

async function challengeFriend() {
  const r = result.value;
  if (!r) return;
  const url = `${shareOrigin()}${challengePath({ seed: r.seed, homesLit: r.homesLit, score: r.score })}`;
  await shareLink(url, 'Switch On Christmas', `I lit ${r.homesLit} ${r.homesLit === 1 ? 'home' : 'homes'} on Switch On Christmas. Can you beat it?`);
}

function backToStreet() {
  if (ionRouter.canGoBack()) ionRouter.back(); else router.replace({ name: 'title' });
}

watch(unlocked, () => nextTick(drawDeco));

onMounted(() => {
  if (!result.value) { router.replace({ name: 'title' }); return; }
  countUp();
  addLight();
  nextTick(drawDeco);
});
</script>

<style scoped>
.results-stack {
  padding-top: var(--sp-2);
}

.section-title.first {
  margin-top: 0;
}

.verdict {
  margin: 0;
  text-align: center;
  font-weight: 600;
  padding: var(--sp-4);
}

.national {
  text-align: center;
}

.national .plus {
  margin: 0 0 var(--sp-1);
  font-size: var(--text-xl);
  font-weight: 800;
  letter-spacing: var(--tracking-tight);
  color: var(--soc-star);
}

.national strong {
  color: var(--ink);
}

.unlocked .deco {
  margin-bottom: var(--sp-3);
  cursor: default;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
}
</style>
